import { alpha, Box, Typography } from "@mui/material";
import { Moment } from "moment";
import { MouseEvent, useEffect, useState } from "react";
import { Task } from "../../../api/Tasks";
import { CalendarAddTask } from "./CalendarAddTask";
import { CalendarTask } from "./CalendarTask";
import { applyOrder } from "../../../misc/dragOrder";
import { groupDayTasks } from "../dayGroups";
import { isToday } from "../../../misc/dates";
import { DragTask } from "./range";

export function CalendarDay(props: {
    date: Moment;
    dateKey: string;
    tasks: Task[];
    muted: boolean;
    alwaysShowAdd: boolean;
    selectedId?: number;

    // selectedIds is the tasks picked for a bulk change
    selectedIds?: number[];
    saving?: boolean;
    showTeam?: boolean;
    dragging: DragTask | null;
    onCreate: (title: string) => void;
    onSelect: (task: Task, e: MouseEvent) => void;
    onContextMenu: (task: Task, e: MouseEvent) => void;
    onToggle: (task: Task, checked: boolean) => void;
    onDragStart: (drag: DragTask) => void;
    onDragEnd: () => void;
    // ids is null when the drop left the day's order alone
    onReorder: (input: { ids: number[] | null; task: Task; date: string }) => void;
    onReschedule: (task: Task, date: string) => void;
}) {
    const today = isToday(props.date);
    const weekend = props.date.day() === 0 || props.date.day() === 6;
    const [hover, setHover] = useState(false);
    const [dropping, setDropping] = useState(false);

    // a drag that started in another day moves the task to this one
    const incoming = !!props.dragging && props.dragging.date !== props.dateKey;
    useEffect(() => {
        if (!props.dragging) setDropping(false);
    }, [props.dragging]);

    // the preview stands in for the server order until the day's tasks change
    const [order, setOrder] = useState<number[] | null>(null);
    const dayTasks = props.tasks.map(task => task.id).sort().join(",");
    useEffect(() => setOrder(null), [dayTasks]);

    // what's been ticked off goes above what's left, and a task carried in from a day
    // that has passed isn't one of this day's own at all, so the add row sits between
    // those two: that's where a new task lands
    const { done, open, carried } = groupDayTasks(applyOrder(props.tasks, order));

    // the day is saved in the order it's drawn, top to bottom
    const drawn = done.concat(open, carried);

    const dragOver = (over: Task) => {
        const drag = props.dragging;
        if (!drag || drag.date !== props.dateKey) return;

        // where a completed task sits is set by when it was ticked off, so neither one
        // being dragged nor one being dragged over takes part in the day's own order
        if (drag.task.completedAt || over.completedAt) return;

        const ids = drawn.map(task => task.id);
        const from = ids.indexOf(drag.task.id);
        const to = ids.indexOf(over.id);
        if (from < 0 || to < 0 || from === to) return;

        ids.splice(from, 1);
        ids.splice(to, 0, drag.task.id);
        setOrder(ids);
    };

    // one task chip, so every group of the day is drawn the same way
    function renderTask(task: Task) {
        return (
            <CalendarTask
                key={task.id}
                task={task}
                showTeam={props.showTeam}
                selected={task.id === props.selectedId || !!props.selectedIds?.includes(task.id)}
                disabled={props.saving}
                onClick={e => props.onSelect(task, e)}
                onContextMenu={e => props.onContextMenu(task, e)}
                onToggle={checked => props.onToggle(task, checked)}
                // an undated checklist item only reaches the calendar once it's ticked
                // off, and the day it sits on is the day it was done rather than a day a
                // drag can choose
                draggable={task.type !== "checklist" || !!task.completedAt || !!task.dueDate}
                onDragStart={e => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", String(task.id));
                    props.onDragStart({ task: task, date: props.dateKey });
                }}
                onDragEnd={props.onDragEnd}
                onDragOver={e => {
                    // a drag from another day belongs to the cell, not the item
                    if (props.dragging?.date !== props.dateKey) return;

                    e.preventDefault();
                    dragOver(task);
                }}
                onDrop={e => {
                    const drag = props.dragging;
                    if (!drag || drag.date !== props.dateKey) return;

                    e.preventDefault();
                    // a drop that moved nothing still counts, since it's what settles
                    // a carried task into the day it was dropped in
                    props.onReorder({ ids: order, task: drag.task, date: props.dateKey });
                    props.onDragEnd();
                }}
            />
        );
    }

    return (
        <Box
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onDragOver={e => {
                if (!incoming) return;

                e.preventDefault();
                setDropping(true);
            }}
            onDragLeave={() => setDropping(false)}
            onDrop={e => {
                setDropping(false);
                if (!incoming || !props.dragging) return;

                e.preventDefault();
                props.onReschedule(props.dragging.task, props.dateKey);
                props.onDragEnd();
            }}
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                minWidth: 0,
                minHeight: 0,
                overflow: "auto",
                p: 0.5,
                borderRight: 1,
                borderBottom: 1,
                borderColor: "grey.300",
                // each kind of day carries its own tint so the grid reads as a grid
                // rather than as one white sheet
                bgcolor: theme => {
                    if (dropping) return alpha(theme.palette.primary.main, 0.16);
                    if (props.muted) return theme.palette.grey[200];
                    if (today) return alpha(theme.palette.primary.main, 0.06);
                    if (weekend) return theme.palette.grey[50];
                    return theme.palette.background.paper;
                },
            }}
        >
            <Typography
                variant="caption"
                align="center"
                sx={{
                    alignSelf: "flex-start",
                    flexShrink: 0,
                    minWidth: 20,
                    py: 0.25,
                    borderRadius: 10,
                    fontWeight: today ? 700 : 600,
                    color: today ? "common.white" : props.muted ? "text.disabled" : "text.secondary",
                    bgcolor: today ? "primary.main" : undefined,
                }}
            >
                {props.date.date()}
            </Typography>

            {done.map(renderTask)}

            {open.map(renderTask)}

            <CalendarAddTask
                visible={props.alwaysShowAdd || hover}
                onCreate={props.onCreate}
            />

            {carried.length > 0 && (
                // the group needs naming, since a carried task sits in a day that isn't
                // the one it's due on
                <Typography
                    variant="caption"
                    sx={{
                        flexShrink: 0,
                        pt: 0.25, px: 0.25,
                        color: "text.disabled",
                        fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
                        textTransform: "uppercase",
                    }}
                >
                    From previous
                </Typography>
            )}

            {carried.map(renderTask)}
        </Box>
    );
}
